import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import { useSupplierStore } from '../../hooks';
import { Button, Card, CardContent, useToast, Badge } from '@lancar-pos/ui';

export function SuppliersPage() {
  const navigate = useNavigate();
  const { suppliers, deleteSupplier } = useSupplierStore();
  const toast = useToast();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<typeof suppliers[0] | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const filteredSuppliers = useMemo(() => {
    return suppliers
      .filter((s) => !s._deleted)
      .filter((s) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          s.name.toLowerCase().includes(query) ||
          s.code.toLowerCase().includes(query) ||
          s.phone.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [suppliers, searchQuery]);

  const handleDelete = (supplier: typeof suppliers[0]) => {
    setSupplierToDelete(supplier);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;
    
    setDeletingId(supplierToDelete.id);
    try {
      await deleteSupplier(supplierToDelete.id);
      toast.success('Supplier dihapus');
    } catch (error) {
      toast.error('Gagal menghapus supplier');
    } finally {
      setDeletingId(null);
      setShowDeleteDialog(false);
      setSupplierToDelete(null);
    }
  };

  const handleAddSupplier = () => {
    window.location.href = '/app/inventory/suppliers/new';
  };

  const handleEditSupplier = (id: string) => {
    window.location.href = `/app/inventory/suppliers/${id}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate({ to: '/app' })}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Supplier</h1>
          </div>
          <Button onClick={handleAddSupplier}>
            <Plus className="w-4 h-4 mr-2" />Tambah Supplier
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 lg:p-6 space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cari supplier..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kode</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontak</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telepon</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        {searchQuery ? 'Supplier tidak ditemukan' : 'Belum ada supplier'}
                      </td>
                    </tr>
                  ) : (
                    filteredSuppliers.map((supplier) => (
                      <tr 
                        key={supplier.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleEditSupplier(supplier.id)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">{supplier.code}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{supplier.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{supplier.contactPerson || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{supplier.phone}</td>
                        <td className="px-4 py-3">
                          <Badge variant={supplier.isActive ? 'success' : 'default'}>
                            {supplier.isActive ? 'Aktif' : 'Tidak Aktif'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSupplier(supplier.id);
                              }}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                              <Edit2 className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(supplier);
                              }}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Hapus Supplier</h3>
            <p className="text-gray-600 mb-4">
              Apakah Anda yakin ingin menghapus supplier "{supplierToDelete?.name}"?
            </p>
            <div className="flex gap-3 justify-end">
              <Button 
                variant="secondary" 
                onClick={() => setShowDeleteDialog(false)}
                disabled={!!deletingId}
              >
                Batal
              </Button>
              <Button 
                variant="danger" 
                onClick={confirmDelete}
                disabled={!!deletingId}
              >
                {deletingId ? 'Menghapus...' : 'Hapus'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
